import { hasSelectedEntity } from '../../../shared/utils/entitySelection';
import { hasCalendarEventField } from '../config/calendarEventTypes';
import { parseCalendarAmount } from './calendar.amount';
import {
  EVENT_TYPE_META,
  PRIORITY_META,
  INITIAL_NEW_EVENT,
} from './calendar.constants';

export function getEventTypeLabel(type) {
  return EVENT_TYPE_META[type]?.label || 'Evento';
}

export function getEventTypeColor(type) {
  return EVENT_TYPE_META[type]?.badgeClass || 'bg-[var(--surface-raised)] text-[var(--text-primary)] border-[var(--border)]';
}

export function getPriorityColor(priority) {
  return PRIORITY_META[priority] || 'border-[var(--border)] bg-[var(--surface-raised)] text-[var(--text-primary)]';
}

export function formatDateLabel(dateString) {
  if (!dateString) return 'Nessuna data selezionata';

  const date = new Date(`${dateString}T00:00:00`);
  return new Intl.DateTimeFormat('it-IT', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function formatTimeLabel(time) {
  return time || '--:--';
}

export function formatCalendarDate(dateString) {
  if (!dateString) return 'N/D';

  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateString;

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function sortEventsByDateTime(events = []) {
  return [...events].sort((a, b) => {
    const aDate = new Date(`${a.date}T${a.time || '00:00'}`);
    const bDate = new Date(`${b.date}T${b.time || '00:00'}`);
    return aDate - bDate;
  });
}

function parseDateKey(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function normalizeDateRange(range = {}) {
  const startDate = range?.startDate || '';
  const endDate = range?.endDate || '';
  if (!startDate && !endDate) return { startDate: '', endDate: '' };
  if (!startDate) return { startDate: endDate, endDate };
  if (!endDate) return { startDate, endDate: '' };

  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  if (!start || !end || start <= end) return { startDate, endDate };
  return { startDate: endDate, endDate: startDate };
}

export function isDateInRange(dateString, range = {}) {
  if (!range?.startDate && !range?.endDate) return true;
  const current = parseDateKey(dateString);
  if (!current) return false;
  const normalizedRange = normalizeDateRange(range);
  const start = parseDateKey(normalizedRange.startDate);
  const end = parseDateKey(normalizedRange.endDate || normalizedRange.startDate);
  if (!start || !end) return true;
  return current >= start && current <= end;
}

export function getDefaultUpcomingEvents(events = [], limit = 15) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return sortEventsByDateTime(events)
    .filter((event) => {
      const date = parseDateKey(event.date);
      return date && date >= today;
    })
    .slice(0, limit);
}

export function hasActiveEventFilters(values = {}, selectedDateRange = {}) {
  const hasRange = Boolean(selectedDateRange?.startDate || selectedDateRange?.endDate);
  return Boolean(
    String(values.query || '').trim() ||
    (Array.isArray(values.type) && values.type.length > 0) ||
    (Array.isArray(values.priority) && values.priority.length > 0) ||
    values.status ||
    values.range ||
    values.staffId ||
    values.clientId ||
    values.supplierId ||
    hasRange
  );
}

export function buildEventDateSet(events = []) {
  return new Set(events.map((event) => event.date).filter(Boolean));
}

export function isSameDay(a, b) {
  return a && b && a === b;
}

export function isWithinNextDays(dateString, days = 15) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const end = new Date(today);
  end.setDate(end.getDate() + days);

  const current = new Date(`${dateString}T00:00:00`);
  return current >= today && current <= end;
}

export function normalizeEventType(type) {
  return type || 'meeting';
}

export function getPriorityLabel(priority) {
  const labels = {
    alta: 'Alta',
    media: 'Media',
    bassa: 'Bassa',
  };

  return labels[priority] || 'Media';
}

export function isEventCompleted(event) {
  const status = String(event?.status || '').toLowerCase();
  return ['completed', 'completato', 'done', 'chiuso', 'pagato', 'incassato'].includes(status);
}

export function getCalendarEventStatus(event) {
  return isEventCompleted(event) ? 'completed' : 'pending';
}

export function formatEventOwner(event) {
  if (event?.ownerName) return event.ownerName;
  if (event?.managedBy) return event.managedBy;
  if (event?.createdBy) return event.createdBy;
  if (event?.sourceLabel) return event.sourceLabel;
  if (Array.isArray(event?.attendees) && event.attendees.length > 0) {
    return `${event.attendees.length} partecipanti`;
  }
  if (event?.isReadOnly) return 'Sistema';
  return 'Calendario';
}

export function formatEventCreator(event) {
  if (event?.createdBy) return event.createdBy;
  if (event?.createdByName) return event.createdByName;
  if (event?.creatorName) return event.creatorName;
  if (event?.creator) return event.creator;
  if (event?.isReadOnly) return 'Sistema';
  return 'Non disponibile';
}

export function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

export function toDateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

export function buildCalendarDays(count = 15, start = new Date()) {
  const normalizedStart = new Date(start);
  normalizedStart.setHours(0, 0, 0, 0);

  return Array.from({ length: count }, (_, index) => {
    const date = addDays(normalizedStart, index);
    return {
      key: toDateKey(date),
      date,
      dayNumber: date.getDate(),
      weekday: new Intl.DateTimeFormat('it-IT', { weekday: 'short' }).format(date),
    };
  });
}

export function buildCalendarMonthDays(monthDate = new Date()) {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

  return Array.from({ length: end.getDate() }, (_, index) => {
    const date = addDays(start, index);
    return {
      key: toDateKey(date),
      date,
      dayNumber: date.getDate(),
      weekday: new Intl.DateTimeFormat('it-IT', { weekday: 'short' }).format(date),
    };
  });
}

export function formatCalendarMonthLabel(monthDate = new Date()) {
  return new Intl.DateTimeFormat('it-IT', {
    month: 'long',
    year: 'numeric',
  }).format(monthDate);
}

function isWithinRange(dateString, range) {
  if (!range) return true;
  if (!dateString) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const current = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(current.getTime())) return false;

  if (range === 'today') {
    return current.getTime() === today.getTime();
  }

  if (range === 'next7' || range === 'next7days') {
    return isWithinNextDays(dateString, 7);
  }

  if (range === 'next15') {
    return isWithinNextDays(dateString, 15);
  }

  if (range === 'month') {
    return current.getMonth() === today.getMonth() && current.getFullYear() === today.getFullYear();
  }

  return true;
}

export function filterCalendarEvents(events = [], values = {}, selectedDate = '') {
  const query = String(values.query || '').trim().toLowerCase();
  const selectedTypes = Array.isArray(values.type) ? values.type : [];
  const selectedPriorities = Array.isArray(values.priority) ? values.priority : [];
  const selectedDateRange = values.selectedDateRange || {};

  return events.filter((event) => {
    const title = String(event.title || '').toLowerCase();
    const description = String(event.description || '').toLowerCase();
    const location = String(event.location || '').toLowerCase();
    const source = String(event.sourceLabel || '').toLowerCase();
    const linkedLabel = String(event.linkedEntity?.label || '').toLowerCase();
    const financialDetails = [
      event.financialDetails?.beneficiary,
      event.financialDetails?.recipient,
      event.financialDetails?.reference,
      event.financialDetails?.status,
      event.clientName,
      event.supplierName,
    ].map((value) => String(value || '').toLowerCase());
    const matchesQuery = query
      ? [title, description, location, source, linkedLabel, ...financialDetails].some((value) => value.includes(query))
      : true;
    const matchesType = selectedTypes.length ? selectedTypes.includes(event.type) : true;
    const matchesPriority = selectedPriorities.length ? selectedPriorities.includes(event.priority || 'media') : true;
    const matchesStatus = values.status ? getCalendarEventStatus(event) === values.status : true;
    const matchesRange = isWithinRange(event.date, values.range);
    const matchesSelectedDate = selectedDate ? event.date === selectedDate : true;
    const matchesSelectedDateRange = isDateInRange(event.date, selectedDateRange);
    const matchesStaff = values.staffId
      ? (event.attendees || []).map(String).includes(String(values.staffId))
      : true;
    const matchesClient = values.clientId
      ? hasSelectedEntity(event, 'client', values.clientId)
      : true;
    const matchesSupplier = values.supplierId
      ? hasSelectedEntity(event, 'supplier', values.supplierId)
      : true;

    return (
      matchesQuery &&
      matchesType &&
      matchesPriority &&
      matchesStatus &&
      matchesRange &&
      matchesSelectedDate &&
      matchesSelectedDateRange &&
      matchesStaff &&
      matchesClient &&
      matchesSupplier
    );
  });
}

export function filterEvents({
  events = [],
  selectedDate = null,
  activeFilters = [],
  showNext15 = false,
}) {
  return events.filter((event) => {
    const matchesDate = selectedDate ? isSameDay(event.date, selectedDate) : true;
    const matchesType =
      activeFilters.length > 0 ? activeFilters.includes(event.type) : true;
    const matchesNext15 = showNext15 ? isWithinNextDays(event.date, 15) : true;

    return matchesDate && matchesType && matchesNext15;
  });
}

export function buildEventPayload(newEvent) {
  return {
    title: newEvent.title.trim(),
    description: newEvent.description.trim(),
    date: newEvent.date,
    time: newEvent.time,
    duration:
      newEvent.duration === 'custom' ? newEvent.durationCustom.trim() : newEvent.duration,
    type: newEvent.type,
    attendees: newEvent.attendees,
    location: newEvent.location.trim(),
    priority: newEvent.priority,
  };
}

export function validateEventForm(newEvent) {
  const errors = {};

  if (!newEvent.title.trim()) errors.title = 'Il titolo è obbligatorio';
  if (!newEvent.date) errors.date = 'La data è obbligatoria';
  if (hasCalendarEventField(newEvent.type, 'time') && !newEvent.time) errors.time = 'L’ora è obbligatoria';
  if (hasCalendarEventField(newEvent.type, 'amount') && Number.isNaN(parseCalendarAmount(newEvent.amount))) {
    errors.amount = 'Inserisci un importo valido, non negativo, con al massimo due decimali';
  }
  if (hasCalendarEventField(newEvent.type, 'endDate') && newEvent.endDate && newEvent.endDate < newEvent.date) {
    errors.endDate = 'La data di fine non può precedere la data di inizio';
  }

  if (hasCalendarEventField(newEvent.type, 'duration') && newEvent.duration === 'custom' && !newEvent.durationCustom.trim()) {
    errors.durationCustom = 'Inserisci una durata custom valida';
  }

  return errors;
}

export function getResetEventForm() {
  return { ...INITIAL_NEW_EVENT };
}

export function getSelectedDateTitle(selectedDate, showNext15) {
  if (selectedDate) return `Eventi del ${formatDateLabel(selectedDate)}`;
  if (showNext15) return 'Eventi dei prossimi 15 giorni';
  return 'Tutti gli eventi';
}
